/**
 * Token Counter
 *
 * Lazily loaded gpt-tokenizer wrapper. The tokenizer is ~430 KB gzipped,
 * so it must not ship with the initial chat bundle.
 *
 * Primary responsibilities:
 * - Count tokens exactly once the tokenizer has loaded
 * - Fall back to a chars/4 estimate until then (display-only callers)
 * - Let async callers await the real tokenizer when exactness matters
 *
 * Dependencies:
 * - gpt-tokenizer (dynamic import)
 */

type Encode = (text: string) => number[]

let encoder: Encode | null = null
let loader: Promise<void> | null = null

const IDLE_PRELOAD_TIMEOUT_MS = 8_000

// --------------

/** Load the tokenizer once; safe to call repeatedly. */
export function ensureTokenizer(): Promise<void> {
  if (encoder) return Promise.resolve()
  if (!loader) {
    loader = import('gpt-tokenizer')
      .then((module) => {
        encoder = module.encode
      })
      .catch((error) => {
        // Allow a later retry if the chunk failed to load
        loader = null
        throw error
      })
  }
  return loader
}

// --------------

/** Estimate used before the tokenizer loads (matches build-prompt budgeting). */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4)
}

// --------------

/**
 * Exact count when the tokenizer is loaded, otherwise an estimate.
 * Use only where an approximate count is acceptable (UI, warnings).
 */
export function countTokens(text: string): number {
  if (!text) return 0
  if (encoder) return encoder(text).length
  void ensureTokenizer().catch(() => {})
  return estimateTokens(text)
}

// --------------

/** Exact count; caller must have awaited ensureTokenizer(). */
export function countTokensExact(text: string): number {
  if (!text) return 0
  if (!encoder) throw new Error('Tokenizer not loaded; await ensureTokenizer() first')
  return encoder(text).length
}

// Warm the tokenizer in the browser once the page is idle, off the
// critical path, so the first exact count rarely has to wait.
if (typeof window !== 'undefined') {
  const preload = () => void ensureTokenizer().catch(() => {})
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(preload, { timeout: IDLE_PRELOAD_TIMEOUT_MS })
  } else {
    setTimeout(preload, IDLE_PRELOAD_TIMEOUT_MS)
  }
}
